import React, { useState } from 'react'
import { sortBy } from 'lodash'
import Box from '@material-ui/core/Box'
import Card from '@material-ui/core/Card'
import CardHeader from '@material-ui/core/CardHeader'
import ContactlessOutlinedIcon from '@material-ui/icons/ContactlessOutlined'
import Typography from '@material-ui/core/Typography'
import Chip from '@material-ui/core/Chip'
import ErrorOutlineOutlinedIcon from '@material-ui/icons/ErrorOutlineOutlined'
import Divider from '@material-ui/core/Divider'
import IconButton from '@material-ui/core/IconButton'
import MoreHorizOutlinedIcon from '@material-ui/icons/MoreHorizOutlined'
import LaunchOutlinedIcon from '@material-ui/icons/LaunchOutlined'
import Menu from '@material-ui/core/Menu'
import MenuItem from '@material-ui/core/MenuItem'
import ManageStripeAccountConfirmationDialog from './manage-stripe-account-confirmation-dialog'
import EditPaymentAccountNameDialog from './edit-payment-account-name-dialog'
import { useIsMobile } from 'lib/hooks'
import {
  INCOMPLETE_STRIPE_SETUP_TIP,
  CHARGES_DISABLED_OR_ACCOUNT_DELETED_TIP,
  PAYOUTS_DISABLED_TIP,
} from 'utils/constants'
import { useStyles } from './styles'

const PaymentAccountActionMenu = ({ paymentAccount, trigger, refetch }) => {
  const classes = useStyles()
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null)
  const [isManageStripeDialogOpen, setIsManageStripeDialogOpen] = useState(
    false,
  )
  const [isEditNameDialogOpen, setIsEditNameDialogOpen] = useState(false)

  const openMenu = e => setActionMenuAnchor(e.target)
  const closeMenu = () => setActionMenuAnchor(null)
  const { detailsSubmitted } = paymentAccount

  return (
    <>
      <trigger.type {...trigger.props} onClick={openMenu} />
      <Menu
        open={Boolean(actionMenuAnchor)}
        onClose={closeMenu}
        anchorEl={actionMenuAnchor}
        disableAutoFocus={true}
        disableAutoFocusItem={true}
        disableRestoreFocus={true}
        disableEnforceFocus={true}
      >
        <MenuItem onClick={() => setIsManageStripeDialogOpen(true)}>
          <Box display="flex">
            <Typography variant="body1" className={classes.menuItemText}>
              {detailsSubmitted ? 'Manage Stripe' : 'Continue Setup'}
            </Typography>
            <LaunchOutlinedIcon
              className={classes.menuItemIcon}
              fontSize="small"
            />
          </Box>
        </MenuItem>
        <MenuItem onClick={() => setIsEditNameDialogOpen(true)}>
          Edit name
        </MenuItem>
      </Menu>
      <ManageStripeAccountConfirmationDialog
        paymentAccount={paymentAccount}
        isOpen={isManageStripeDialogOpen}
        onClose={() => {
          closeMenu()
          setIsManageStripeDialogOpen(false)
        }}
      />
      <EditPaymentAccountNameDialog
        paymentAccount={paymentAccount}
        isOpen={isEditNameDialogOpen}
        onClose={() => {
          closeMenu()
          setIsEditNameDialogOpen(false)
        }}
        onSuccess={() => {
          closeMenu()
          setIsEditNameDialogOpen(false)
          refetch()
        }}
      />
    </>
  )
}

const PaymentAccountRow = ({ paymentAccount, refetch }) => {
  const classes = useStyles()
  const isMobile = useIsMobile()

  const {
    name,
    detailsSubmitted,
    chargesEnabled,
    payoutsEnabled,
  } = paymentAccount
  const isFullySetUp = detailsSubmitted && chargesEnabled && payoutsEnabled

  const getChipsForAccount = () => {
    const chips = []
    if (isFullySetUp) {
      chips.push(
        <Chip
          key="active"
          label="Active"
          size={isMobile ? 'small' : 'medium'}
          className={classes.activeChip}
        />,
      )
      return chips
    } else if (!detailsSubmitted) {
      chips.push(
        <Chip
          key="incomplete"
          label="Stripe Setup Incomplete"
          size={isMobile ? 'small' : 'medium'}
          className={classes.stripeSetupIncompleteChip}
          icon={<ErrorOutlineOutlinedIcon className={classes.errorIcon} />}
        />,
      )
      return chips
    }
    if (!chargesEnabled) {
      chips.push(
        <Chip
          key="unable"
          label="Unable to Accept Payments"
          size={isMobile ? 'small' : 'medium'}
          className={classes.unableToAcceptPaymentsChip}
          icon={<ErrorOutlineOutlinedIcon className={classes.errorIcon} />}
        />,
      )
    }
    if (!payoutsEnabled) {
      chips.push(
        <Chip
          key="payouts"
          label="Payouts on Hold"
          size={isMobile ? 'small' : 'medium'}
          className={classes.payoutsOnHoldChip}
          icon={<ErrorOutlineOutlinedIcon className={classes.errorIcon} />}
        />,
      )
    }
    return chips
  }

  const getWarningBlocks = () => {
    const warnings = []
    if (!detailsSubmitted) {
      warnings.push(
        <Box
          data-testid="details-not-submitted-warning"
          className={`${classes.warningBox} ${classes.detailsNotSubmittedWarning}`}
        >
          {INCOMPLETE_STRIPE_SETUP_TIP}
        </Box>,
      )
      return warnings
    }
    if (!chargesEnabled) {
      warnings.push(
        <Box
          data-testid="charges-disabled-warning"
          className={`${classes.warningBox} ${classes.chargesDisabledWarning}`}
        >
          {CHARGES_DISABLED_OR_ACCOUNT_DELETED_TIP}
        </Box>,
      )
    }
    if (!payoutsEnabled) {
      warnings.push(
        <Box
          data-testid="payouts-disabled-warning"
          className={`${classes.warningBox} ${classes.payoutsDisabledWarning}`}
        >
          {PAYOUTS_DISABLED_TIP}
        </Box>,
      )
    }
    return warnings
  }

  return (
    <>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        py={4}
      >
        <Box display="flex" flexDirection="column">
          <Box display="flex" alignItems="center">
            <Box mr={4.25}>
              <ContactlessOutlinedIcon
                data-testid="contactless-icon"
                size="small"
              />
            </Box>
            <Typography variant="body1">{name}</Typography>
          </Box>
          {isMobile && (
            <Box mt={1} ml={7.25}>
              {getChipsForAccount()}
            </Box>
          )}
        </Box>
        {!isMobile && (
          <Box
            flexGrow={1}
            display="flex"
            flexDirection="row"
            flexWrap="wrap"
            justifyContent="flex-end"
            alignItems="center"
            mr={1}
          >
            {getChipsForAccount()}
          </Box>
        )}
        <Box flexShrink={1}>
          <PaymentAccountActionMenu
            paymentAccount={paymentAccount}
            trigger={
              <IconButton>
                <MoreHorizOutlinedIcon color="action" aria-label="more" />
              </IconButton>
            }
            refetch={refetch}
          />
        </Box>
      </Box>
      {getWarningBlocks()}
    </>
  )
}

const PaymentAccountsDisplay = ({ paymentAccounts, refetch }) => {
  const classes = useStyles()
  return (
    <Card variant="outlined">
      <CardHeader
        title="Payment Accounts"
        titleTypographyProps={{ variant: 'h6' }}
        className={classes.cardHeader}
      />
      <Box px={4}>
        {sortBy(paymentAccounts, ['name']).map((pa, idx) => (
          <Box key={pa.id}>
            {idx > 0 && <Divider />}
            <PaymentAccountRow paymentAccount={pa} refetch={refetch} />
          </Box>
        ))}
      </Box>
    </Card>
  )
}

export default PaymentAccountsDisplay
